import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concat, from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import {
	COUPON_UPDATE_APPLIED,
	COUPON_APPLY,
	COUPON_CLEAR_STATE,
	COUPON_FAILURE,
	COUPON_FIND_ALL,
	COUPON_FIND_ALL_SUCCESS,
	COUPON_RESET,
	COUPON_UPDATE_LOADING,
	COUPON_VALID,
	COUPON_VALIDATED_SUCCESS,
} from '../actions/coupon.action';
import { CART_FIND_TOTALS, NOTIFICATION_SHOW } from '../actions';
import { CartService, CouponService } from '@core/services';
import {
	selectCartTotals,
	selectLocalChannelId,
} from '@store/selectors';
import { selectAppliedCoupon } from '@store/selectors/coupon.selectors';
import { NotificationEnum } from '@shared/components/notification/enum/notification.enum';
import { CheckoutOrderProcessor } from '@/features/checkout/domain/processor';
import { canApplyCoupon, canValidateCoupon } from '@/features/checkout/domain/util';

@Injectable()
export class CouponEffects {
	constructor(
		private actions$: Actions,
		private store: Store,
		private couponService: CouponService,
		private checkoutOrderProcessor: CheckoutOrderProcessor,
		private cartService: CartService,
	) { }

	findAllCoupons$ = createEffect(() =>
		this.actions$.pipe(
			ofType(COUPON_FIND_ALL),
			mergeMap(() =>
				this.couponService.findAll().pipe(
					map(({ data: couponList }) => COUPON_FIND_ALL_SUCCESS({ couponList })),
					catchError(error => of(COUPON_FAILURE({ error })))
				)
			)
		)
	);

	applyCoupon$ = createEffect(() =>
		this.actions$.pipe(
			ofType(COUPON_APPLY),
			withLatestFrom(
				this.store.select(selectAppliedCoupon),
				this.store.select(selectLocalChannelId)
			),
			map(([_, appliedCoupon, localChannelId]) => ({
				appliedCoupon,
				localChannelId,
			})),
			filter(canApplyCoupon),
			mergeMap(({ appliedCoupon, localChannelId }) =>
				concat(
					of(COUPON_UPDATE_LOADING({ loading: true })),
					this.couponService.applyCoupon(appliedCoupon, localChannelId).pipe(
						mergeMap(({ data: response }) =>
							of(
								COUPON_FIND_ALL_SUCCESS({ couponList: response.couponList }),
								COUPON_UPDATE_LOADING({ loading: false })
							)
						),
						catchError(error =>
							of(
								COUPON_UPDATE_LOADING({ loading: false }),
								NOTIFICATION_SHOW({
									notification: {
										type: NotificationEnum.Error,
										title: NotificationEnum.Error,
										detail: error.message,
									},
								}),
								COUPON_CLEAR_STATE()
							)
						)
					)
				)
			)
		)
	);

	validCoupon$ = createEffect(() =>
		this.actions$.pipe(
			ofType(COUPON_VALID),
			withLatestFrom(
				this.store.select(selectLocalChannelId),
				this.store.select(selectCartTotals),
				this.cartService.getStoreForStore(),
			),
			map(([action, localChannelId, cartTotals, cartList]) => {
				const coupon = action.coupon;

				return {
					coupon,
					localChannelId,
					cartList,
					cartTotals,
				};
			}),
			filter(canValidateCoupon),
			switchMap(({ coupon, localChannelId, cartList, cartTotals }) =>
				concat(
					of(COUPON_UPDATE_LOADING({ loading: true })),
					this.couponService
						.validCoupon(coupon, localChannelId, cartList ?? [], cartTotals)
						.pipe(
							mergeMap(({ data }) => {
								const result = this.checkoutOrderProcessor.processCouponValidation({
									coupon,
									validation: data,
									cartSubtotal: cartTotals?.subtotal ?? 0n,
								});

								return [
									COUPON_VALIDATED_SUCCESS({ coupon: result.validatedCoupon }),
									COUPON_UPDATE_APPLIED({ code: coupon.code || '' }),
									COUPON_UPDATE_LOADING({ loading: false }),
									CART_FIND_TOTALS(),
								];
							}),
							catchError(error => {
								return from([
									COUPON_RESET(),
									COUPON_UPDATE_LOADING({ loading: false }),
									NOTIFICATION_SHOW({
										notification: {
											type: NotificationEnum.Error,
											title: 'Error',
											detail: error?.['message'] ?? 'Unable to validate coupon.',
										},
									}),
								CART_FIND_TOTALS()
							])}
						)
					)
				)
			)
		)
	);
}
